import { Badge } from '@/shared/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { ChevronsUpDown, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectAutocompleteProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  /**
   * Overrides the trigger's `data-testid`. Needed where a page renders more than
   * one of these side by side (Schedule: Teams + Areas, SC-6251) — the
   * placeholder can't identify them once a selection hides it.
   */
  triggerTestId?: string;
}

// The trigger is a single fixed-height row (to line up with sibling Select
// filters). Chips that don't fit collapse into a "+N" pill instead of wrapping
// and spilling out of the box (WRK-5304). These reserve room inside the trigger
// for the chevron, the overflow pill, and a small always-clickable input.
const CHEVRON_WIDTH = 22; // icon + gap
const OVERFLOW_PILL_WIDTH = 46; // "+N" badge, generous for up to 3 digits
const INPUT_MIN_WIDTH = 28;
const CHIP_GAP = 4; // gap-1

export function MultiSelectAutocomplete({
  options,
  selected,
  onChange,
  placeholder = 'Search...',
  className = '',
  triggerTestId = 'multiselect-trigger',
}: MultiSelectAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(selected.length);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const filtered = options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(search.toLowerCase()) && !selected.includes(opt.value),
  );

  const selectedOptions = options.filter((opt) => selected.includes(opt.value));
  const selectedKey = selectedOptions.map((opt) => opt.value).join('|');

  const shownCount = Math.min(visibleCount, selectedOptions.length);
  const shownOptions = selectedOptions.slice(0, shownCount);
  const hiddenOptions = selectedOptions.slice(shownCount);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Measure how many selected chips fit on the single trigger line; the rest
  // collapse into the "+N" pill. A hidden layer renders every chip at full
  // width so the measurement is independent of what is currently shown.
  // Keyed on selectedKey (a stable string) rather than the selectedOptions
  // array, which the parent recreates every render and would otherwise rebuild
  // the ResizeObserver on each pass.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedKey is the stable derivation of selectedOptions used inside
  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const measure = measureRef.current;
    if (!trigger || !measure) return;

    const compute = () => {
      const total = selectedOptions.length;
      if (total === 0) {
        setVisibleCount((prev) => (prev === 0 ? prev : 0));
        return;
      }

      const widths = (Array.from(measure.children) as HTMLElement[]).map((el) => el.offsetWidth);
      const styles = getComputedStyle(trigger);
      const paddingX =
        Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight);
      const available = trigger.clientWidth - paddingX - CHEVRON_WIDTH - INPUT_MIN_WIDTH;

      // Everything fits without needing an overflow pill.
      const sumAll = widths.reduce((sum, w) => sum + w + CHIP_GAP, 0);
      if (sumAll <= available) {
        setVisibleCount((prev) => (prev === total ? prev : total));
        return;
      }

      // Reserve room for the "+N" pill and fit as many chips as possible.
      const budget = available - OVERFLOW_PILL_WIDTH;
      let used = 0;
      let count = 0;
      for (const w of widths) {
        used += w + CHIP_GAP;
        if (used > budget) break;
        count++;
      }
      // Always keep at least one chip visible so the control isn't just "+N".
      const next = Math.max(1, count);
      setVisibleCount((prev) => (prev === next ? prev : next));
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [selectedKey]);

  // Close the overflow popover once nothing is hidden anymore (e.g. after
  // removing the hidden chips from inside it).
  useEffect(() => {
    if (hiddenOptions.length === 0 && overflowOpen) {
      setOverflowOpen(false);
    }
  }, [hiddenOptions.length, overflowOpen]);

  const handleSelect = (value: string) => {
    onChange([...selected, value]);
    setSearch('');
    inputRef.current?.focus();
  };

  const handleRemove = (value: string) => {
    onChange(selected.filter((v) => v !== value));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && search === '' && selected.length > 0) {
      onChange(selected.slice(0, -1));
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        ref={triggerRef}
        data-testid={triggerTestId}
        className="flex items-center gap-1 px-2 py-1 border border-border rounded-md bg-background cursor-text h-10 overflow-hidden"
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        <div className="flex flex-nowrap items-center gap-1 flex-1 min-w-0 overflow-hidden">
          {shownOptions.map((opt) => (
            <Badge
              key={opt.value}
              variant="outline"
              data-testid="multiselect-chip"
              className="gap-1 py-0.5 px-2 text-xs font-normal shrink-0 max-w-[140px]"
            >
              <span className="truncate">{opt.label}</span>
              <button
                type="button"
                className="ml-0.5 hover:text-destructive shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(opt.value);
                }}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}

          {hiddenOptions.length > 0 && (
            <Popover open={overflowOpen} onOpenChange={setOverflowOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="multiselect-overflow-pill"
                  aria-label={`Show ${hiddenOptions.length} more selected`}
                  className="shrink-0 inline-flex items-center rounded-full border border-border bg-muted/60 px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted"
                  onClick={(e) => e.stopPropagation()}
                >
                  +{hiddenOptions.length}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={6}
                data-testid="multiselect-overflow-popover"
                className="w-auto max-w-[280px] p-2"
                onOpenAutoFocus={(e) => e.preventDefault()}
                // Removing a chip unmounts the focused ✕ button, which Radix
                // would treat as focus leaving the popover and auto-close it.
                // Keep it open — it only dismisses on an outside click / Escape.
                onFocusOutside={(e) => e.preventDefault()}
              >
                <div className="flex flex-wrap gap-1 max-h-[200px] overflow-auto">
                  {hiddenOptions.map((opt) => (
                    <Badge
                      key={opt.value}
                      variant="outline"
                      className="gap-1 py-0.5 px-2 text-xs font-normal max-w-[240px]"
                    >
                      <span className="truncate">{opt.label}</span>
                      <button
                        type="button"
                        className="ml-0.5 hover:text-destructive shrink-0"
                        onClick={() => handleRemove(opt.value)}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          <input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            // Don't open the search dropdown when focus is bounced back here by
            // removing a chip inside the overflow popover (avoids the flicker).
            onFocus={() => {
              if (!overflowOpen) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={selected.length === 0 ? placeholder : ''}
            className={`flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground ${
              selected.length === 0 ? 'min-w-[60px]' : 'min-w-[28px]'
            }`}
          />
        </div>
        <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      </div>

      {/* Hidden measurement layer: every selected chip at full width, used to
          decide how many fit before collapsing into "+N". */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 -z-10 flex gap-1"
      >
        {selectedOptions.map((opt) => (
          <span
            key={opt.value}
            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-normal max-w-[140px]"
          >
            <span className="truncate">{opt.label}</span>
            <X className="w-3 h-3 ml-0.5 shrink-0" />
          </span>
        ))}
      </div>

      {/* The search dropdown and the overflow popover must never show at once:
          removing a chip in the popover bounces focus back to this input, whose
          onFocus would otherwise flash the dropdown open on every removal. */}
      {open && !overflowOpen && (filtered.length > 0 || search) && (
        <div className="absolute z-50 mt-1 w-full max-h-[200px] overflow-auto rounded-md border border-border bg-popover shadow-md">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">No results</div>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/50 text-left"
                onClick={() => handleSelect(opt.value)}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
